import React, { useEffect, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { State } from 'centrifuge';
import { captureException, withScope } from '@sentry/react';
import { useInjection } from 'inversify-react';
import { useAuth } from 'context/Auth';
import { ESentryTag } from 'utils/sentry';
import { EGlobalDIKeys } from 'core/di/keys';
import type { IWebSocketService } from 'api/apiServices/ws/WebSocketService';
import { IWebsockets, nonErrorsCodes } from './types';

const Websockets = React.createContext<IWebsockets>({} as IWebsockets);

export const WebsocketsProvider: React.FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  const queryClient = useQueryClient();
  const { token, provider } = useAuth();

  const webSocketService = useInjection<IWebSocketService>(
    EGlobalDIKeys.WebSocketService,
  );

  // Используем клиент из WebSocketService вместо создания нового
  const ws = useMemo(() => {
    // Инициализируем WebSocketService с токеном
    webSocketService.initialize(token, provider.getPromisifiedToken.bind(provider));

    return webSocketService.getClient();
  }, [token, provider, webSocketService]);

  useEffect(() => {
    if (ws) {
      if (ws.state === State.Disconnected) {
        ws.connect();
      }

      ws.on('publication', ({ data, channel }) => {
        const { messageData, messageType } = data;
        queryClient.setQueryData([messageType, messageData.id], messageData);
        queryClient.setQueryData([channel], messageData);
      });

      ws.on('connected', data => {
        console.log('connect', data);
      });

      ws.on('error', event => {
        if (nonErrorsCodes.includes(event.error.code)) return;

        withScope(scope => {
          scope.setLevel('warning');
          scope.setTag(ESentryTag.WhereWasCaught, 'Websockets provider');
          scope.setExtra(
            'message',
            `Captured websockets error code ${event.error.code} with message ${event.error.message}`,
          );
          captureException(event);
        });
      });

      return () => {
        ws.disconnect();
      };
    }
  }, [queryClient, ws]);

  const value = useMemo<IWebsockets>(() => ({ ws: ws || ({} as any) }), [ws]);

  return <Websockets.Provider value={value}>{children}</Websockets.Provider>;
};
